import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./BranchManagerDashboard.css";
import "./MonthlyReport.css";
import MonthlyReport from "./UserMonthlyReport";
import ProfilePage from "./ProfilePage";
import {
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiLock,
  FiAward,
  FiTarget,
  FiUsers,
  FiSettings,
  FiTrendingUp,
  FiBell,
  FiFileText,
  FiChevronLeft,
  FiPhoneCall,
  FiCheckSquare,
} from "react-icons/fi";
import {
  Box,
  Alert,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Snackbar,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";
import api from "../api/axiosConfig";

import SalesTracker from "./SalesTracker";
import BranchManagerUserManagement from "./BranchManagerUserManagement";
import defaultAvatar from "../images/download.png";
import logo1 from "../images/logo.jpg";

const toTitleCase = (str) => {
  if (!str) return "";
  const withSpaces = str.replace(/_/g, " ").replace(/([A-Z])/g, " $1");
  return withSpaces.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isUserOnLeaveOnDate = (user, dateStr) => {
  if (!user || !user.leaveStatus || !user.leaveStartDate) return false;
  const checkDate = new Date(dateStr + "T00:00:00");
  const startDate = new Date(user.leaveStartDate);
  const endDate = user.leaveEndDate ? new Date(user.leaveEndDate) : null;

  checkDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);

  if (endDate) {
    return checkDate >= startDate && checkDate <= endDate;
  }
  return checkDate >= startDate;
};

// Function to check if a date is a government holiday in India
const isIndianGovernmentHoliday = (date, corporateHolidays = []) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Check against corporate holidays from API
  const holidayFromAPI = corporateHolidays.find((holiday) => {
    const holidayDate = new Date(holiday.date);
    return (
      holidayDate.getFullYear() === year &&
      holidayDate.getMonth() === month &&
      holidayDate.getDate() === day
    );
  });

  if (holidayFromAPI) {
    return {
      name: holidayFromAPI.name,
      type: holidayFromAPI.type,
    };
  }

  // Fallback to basic fixed holidays if API fails (for redundancy)
  const fixedHolidays = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 11, day: 25, name: "Christmas" },
    { month: 4, day: 1, name: "Labour Day" },
  ];

  const fixedHoliday = fixedHolidays.find(
    (holiday) => holiday.month === month && holiday.day === day
  );
  if (fixedHoliday) {
    return {
      name: fixedHoliday.name,
      type: "national",
    };
  }

  return null;
};

// Moved outside the component for performance and to prevent stale closures.
const getMetric = (member = {}, key) => {
  if (!member || !key) return 0;
  const dataKey = key === "dailyPoints" ? "aggregatedPoints" : key;
  const value = member[dataKey];
  if (value !== undefined && value !== null) {
    const parsed = Number(value);
    return !Number.isNaN(parsed) ? parsed : 0;
  }
  return 0;
};

const BranchManagerDashboard = () => {
  const navigate = useNavigate();
  const { section, "*": restOfUrl } = useParams();
  const currentSection = section || "dashboard";

  let targetUserId = "me";
  let entryDateForTracker = formatDateForInput(new Date());

  if (section === "input-for" && restOfUrl) {
    const parts = restOfUrl.split("/");
    targetUserId = parts[0];
    if (parts[1]) entryDateForTracker = parts[1];
  } else if (section === "input" && restOfUrl) {
    entryDateForTracker = restOfUrl;
  }

  // Granular loading and error states for better UX
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBranchDetailsLoading, setIsBranchDetailsLoading] = useState(false);
  const [isMyPerfLoading, setIsMyPerfLoading] = useState(false);
  const [isBranchPerfLoading, setIsBranchPerfLoading] = useState(false);
  const [isBranchCalendarLoading, setIsBranchCalendarLoading] = useState(false);

  // State variables
  const [dashboardData, setDashboardData] = useState(null);
  const [branchPerformanceData, setBranchPerformanceData] = useState([]);
  const [branchSubmissionHistory, setBranchSubmissionHistory] = useState([]);
  const [branchMembers, setBranchMembers] = useState([]);
  const [user, setUser] = useState({});
  const [todaysStatus, setTodaysStatus] = useState({});
  const [kpis, setKpis] = useState({});
  const [performanceTrend, setPerformanceTrend] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [approvedLeaveRequests, setApprovedLeaveRequests] = useState([]);
  const [ownLeaveRequests, setOwnLeaveRequests] = useState([]);
  const [branchSubmissions, setBranchSubmissions] = useState([]);

  // Add monthly KPI data state for current and previous month comparison
  const [monthlyKpiData, setMonthlyKpiData] = useState({
    kpis: {},
    previousMonthKpis: {},
  });

  // Date range states
  const [branchDetailsStartDate, setBranchDetailsStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [branchDetailsEndDate, setBranchDetailsEndDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );
  const [myPerformanceStartDate, setMyPerformanceStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [myPerformanceEndDate, setMyPerformanceEndDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );
  const [branchPerformanceStartDate, setBranchPerformanceStartDate] = useState(
    () => {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
  );
  const [branchPerformanceEndDate, setBranchPerformanceEndDate] = useState(
    new Date()
  );
  const [branchCalendarStartDate, setBranchCalendarStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [branchCalendarEndDate, setBranchCalendarEndDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );

  // UI and control states
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState("");
  const [userName] = useState(localStorage.getItem("name") || "User");
  const userRole = localStorage.getItem("userRole");
  const [selectedField1, setSelectedField1] = useState("dailyPoints");
  const [selectedField2, setSelectedField2] = useState("none");
  const [selectedLeaderboardMetric, setSelectedLeaderboardMetric] =
    useState("aggregatedPoints");
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("pending-approvals");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Corporate holidays state
  const [corporateHolidays, setCorporateHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  // Dialog and form states
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRejectDialogOpen, setLeaveRejectDialogOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [leaveRejectionReason, setLeaveRejectionReason] = useState("");
  const [leaveRevocationReason, setLeaveRevocationReason] = useState("");
  const [leaveRevokeDialogOpen, setLeaveRevokeDialogOpen] = useState(false);
  const [selectedLeaveRevokeRequestId, setSelectedLeaveRevokeRequestId] =
    useState(null);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [leaveDurationDays, setLeaveDurationDays] = useState(0);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [selectedTeamForCalendar, setSelectedTeamForCalendar] = useState(null);
  const [viewingTLId, setViewingTLId] = useState(null);
  const [selectedPerfMetric, setSelectedPerfMetric] = useState("totalPoints");
  const [chartData, setChartData] = useState([]);
  const [showChartTeamDialog, setShowChartTeamDialog] = useState(false);
  const [showMissingEntryModal, setShowMissingEntryModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubmittedList, setModalSubmittedList] = useState([]);
  const [modalMissingList, setModalMissingList] = useState([]);
  const [modalOnLeaveList, setModalOnLeaveList] = useState([]);
  const [calendarDataLoaded, setCalendarDataLoaded] = useState(false);
  const [calendarMembers, setCalendarMembers] = useState([]);
  const [viewingDirectRMsInTable, setViewingDirectRMsInTable] = useState(false);
  const [viewingDirectRMsInRankings, setViewingDirectRMsInRankings] =
    useState(false);
  const [viewingDirectRMsInChart, setViewingDirectRMsInChart] = useState(false);
  const [viewingDirectRMsInCalendar, setViewingDirectRMsInCalendar] =
    useState(false);
  const [directRMs, setDirectRMs] = useState([]);

  const chartFieldOptions = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
    { value: "none", label: "None" },
  ];
  const leaderboardFieldOptions = [
    { value: "aggregatedPoints", label: "Aggregated Points" },
    ...chartFieldOptions.filter((option) => option.value !== "dailyPoints"),
  ];
  const dailyPerformanceChartMetrics = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
  ];
  const performanceChartMetrics = [
    { value: "totalPoints", label: "Total Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
  ];

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  // --- START: Refactored Data Fetching ---

  // Targeted function to refresh leave and notification data.
  const fetchLeaveAndNotifications = useCallback(async () => {
    await Promise.allSettled([
      api
        .get("/api/users/me/my-leave-requests")
        .then((res) => setOwnLeaveRequests(res.data || [])),
      api
        .get("/api/users/subordinate-leave-requests")
        .then((res) => setPendingLeaveRequests(res.data || [])),
      api
        .get("/api/users/branch-leave-requests/approved")
        .then((res) => setApprovedLeaveRequests(res.data || [])),
      api
        .get("/api/users/me/notifications")
        .then((res) => setNotifications(res.data || [])),
    ]);
  }, []);

  // Fetch data that doesn't depend on date ranges, called once on mount.
  const fetchInitialStaticData = useCallback(async () => {
    setIsInitialLoading(true);
    await Promise.allSettled([
      api
        .get(`/api/users/me?t=${new Date().getTime()}`)
        .then((res) => {
          const userData = res.data;
          if (userData) {
            const photoPath = userData.profilePhoto || userData.profile_photo;
            let fullUrl = defaultAvatar;
            if (photoPath) {
              if (
                photoPath.startsWith("http://") ||
                photoPath.startsWith("https://")
              ) {
                fullUrl = photoPath;
              } else {
                const serverOrigin = new URL(api.defaults.baseURL).origin;
                const cleanPath = photoPath.replace(/^\/+/, "");

                fullUrl = `${serverOrigin}/${cleanPath}`;
              }
            }
            setUser({ ...userData, profile_photo: fullUrl });
            localStorage.setItem("name", userData.name || userData.username);
            localStorage.setItem("userId", userData.userId);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user profile for header:", err);
          setUser((prev) => ({ ...prev, profile_photo: defaultAvatar }));
        }),
      api
        .get("/api/users/me/leave-status")
        .then((res) => {
          setIsOnLeave(res.data.leaveStatus);
          if (res.data.leaveStatus && res.data.leaveStartDate) {
            setLeaveDurationDays(
              Math.max(
                1,
                Math.floor(
                  (new Date() - new Date(res.data.leaveStartDate)) / 86400000
                ) + 1
              )
            );
            setLeaveEndDate(res.data.leaveEndDate || null);
          }
        })
        .catch(() => {}),
      fetchLeaveAndNotifications(),
      api
        .get("/api/users/me/branch-submissions")
        .then((res) => setBranchSubmissions(res.data || []))
        .catch((err) =>
          console.error("Failed to load branch submissions", err)
        ),
    ]);
    setIsInitialLoading(false);
  }, [fetchLeaveAndNotifications]);

  // Initial data load on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await fetchInitialStaticData();
        if (["dashboard", "input"].includes(currentSection)) {
          // Fetch the static KPIs for the current month
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          const kpiRes = await api.get(
            `/api/users/me/dashboard?startDate=${formatDateForInput(
              firstDay
            )}&endDate=${formatDateForInput(lastDay)}`
          );
          if (kpiRes.data) {
            setMonthlyKpiData({
              kpis: kpiRes.data.kpis,
              previousMonthKpis: kpiRes.data.previousMonthKpis,
            });
          }
        }
      } catch (e) {
        console.error("Error on initial fetch", e);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [fetchInitialStaticData, currentSection]);

  // Fetch Branch Member Details when its date range changes
  useEffect(() => {
    setIsBranchDetailsLoading(true);
    api
      .get(`/api/users/me/subordinates`)
      .then((res) => setBranchMembers(res.data || []))
      .catch((err) => {
        console.error("Failed to load Branch Member details:", err);
        setError("Could not load Branch Member details.");
      })
      .finally(() => setIsBranchDetailsLoading(false));
  }, []);

  // Fetch RM data when team selection changes
  // Fetch RM or TL data when team selection or view mode changes
  useEffect(() => {
    const fetchCalendarMembers = async () => {
      try {
        if (viewingDirectRMsInCalendar) {
          // ✅ Match direct RMs by reports_to_id = branch manager userId
          const directRMs = branchMembers.filter(
            (member) =>
              member.role === "relationship_manager" &&
              member.reportsToRole === "branch_manager" &&
              String(member.reportsToId) === localStorage.getItem("userId")
          );

          if (directRMs.length > 0) {
            setDirectRMs(directRMs);
            setCalendarMembers(directRMs);
          } else {
            // Fallback: fetch directly from backend if branchMembers is not yet populated
            const res = await api.get(
              `/api/users/branch-detailed-submission-history?directRMs=true`
            );
            const directRMIds = new Set(res.data.map((r) => String(r.userId)));
            const matchedRMs = branchMembers.filter((m) =>
              directRMIds.has(String(m.userId))
            );

            setDirectRMs(matchedRMs);
            setCalendarMembers(matchedRMs);
          }
        } else if (selectedTeamForCalendar) {
          // ✅ Fetch RMs for selected Team Lead calendar view (TL himself should not be included)
          const res = await api.get(
            `/api/users/rms-by-team-lead/${selectedTeamForCalendar.userId}`
          );
          setCalendarMembers(res.data || []);
        } else {
          // ✅ Default: show Team Leads for the branch-wide view
          const teamLeads = branchMembers.filter((m) => m.role === "team_lead");
          setCalendarMembers(teamLeads);
        }
      } catch (err) {
        console.error("❌ Failed to load calendar members:", err);
        setCalendarMembers([]);
      }
    };

    fetchCalendarMembers();
  }, [selectedTeamForCalendar, branchMembers, viewingDirectRMsInCalendar]);

  // Fetch "My Performance" data when its date range changes
  useEffect(() => {
    setIsMyPerfLoading(true);
    api
      .get(
        `/api/users/me/dashboard?startDate=${formatDateForInput(
          myPerformanceStartDate
        )}&endDate=${formatDateForInput(
          myPerformanceEndDate
        )}&clientToday=${formatDateForInput(new Date())}`
      )
      .then((res) => {
        setDashboardData(res.data);
        setKpis(res.data.kpis || {});
        setPerformanceTrend(res.data.performanceTrend || []);
        setSubmissionHistory(res.data.submissionHistory || {});
        setTodaysStatus(res.data.todaysStatus || {});
      })
      .catch((err) => console.error("Failed to load My Performance data:", err))
      .finally(() => setIsMyPerfLoading(false));
  }, [myPerformanceStartDate, myPerformanceEndDate]);

  // Fetch Branch Performance data when its date range changes
  useEffect(() => {
    setIsBranchPerfLoading(true);
    api
      .get(
        `/api/users/branch-performance?startDate=${formatDateForInput(
          branchPerformanceStartDate
        )}&endDate=${formatDateForInput(branchPerformanceEndDate)}`
      )
      .then((res) => setBranchPerformanceData(res.data || []))
      .catch((err) =>
        console.error("Failed to load Branch Performance data:", err)
      )
      .finally(() => setIsBranchPerfLoading(false));
  }, [branchPerformanceStartDate, branchPerformanceEndDate]);

  // Fetch Branch Submission Calendar data when its date range changes or team selection changes
  useEffect(() => {
    setIsBranchCalendarLoading(true);
    let url = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
      branchCalendarStartDate
    )}&endDate=${formatDateForInput(
      branchCalendarEndDate
    )}&t=${new Date().getTime()}`;

    // If viewing a specific team lead's calendar, include teamLeadId parameter
    if (selectedTeamForCalendar) {
      url += `&teamLeadId=${selectedTeamForCalendar.userId}`;
    }

    // If viewing direct RMs, include directRMs parameter
    if (viewingDirectRMsInCalendar) {
      url += `&directRMs=true`;
    }

    api
      .get(url)
      .then((res) => {
        setBranchSubmissionHistory(res.data || []);
        setCalendarDataLoaded(true);
      })
      .catch((err) =>
        console.error("Failed to load Branch Submission History:", err)
      )
      .finally(() => setIsBranchCalendarLoading(false));
  }, [
    branchCalendarStartDate,
    branchCalendarEndDate,
    selectedTeamForCalendar,
    viewingDirectRMsInCalendar,
  ]);

  const reloadDataForSection = useCallback(async () => {
    // Refresh KPI data, personal performance data, and calendar data after entry submission
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // Refresh monthly KPI data
      const kpiRes = await api.get(
        `/api/users/me/dashboard?startDate=${formatDateForInput(
          firstDay
        )}&endDate=${formatDateForInput(lastDay)}`
      );
      if (kpiRes.data) {
        setMonthlyKpiData({
          kpis: kpiRes.data.kpis,
          previousMonthKpis: kpiRes.data.previousMonthKpis,
        });
      }

      // Refresh personal performance data (for submission history and performance chart)
      setIsMyPerfLoading(true);
      const perfRes = await api.get(
        `/api/users/me/dashboard?startDate=${formatDateForInput(
          myPerformanceStartDate
        )}&endDate=${formatDateForInput(
          myPerformanceEndDate
        )}&clientToday=${formatDateForInput(new Date())}`
      );
      if (perfRes.data) {
        setDashboardData(perfRes.data);
        setKpis(perfRes.data.kpis || {});
        setPerformanceTrend(perfRes.data.performanceTrend || []);
        setSubmissionHistory(perfRes.data.submissionHistory || {});
        setTodaysStatus(perfRes.data.todaysStatus || {});
      }
      setIsMyPerfLoading(false);

      // Refresh calendar data
      setIsBranchCalendarLoading(true);
      let url = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
        branchCalendarStartDate
      )}&endDate=${formatDateForInput(
        branchCalendarEndDate
      )}&t=${new Date().getTime()}`;

      // If viewing a specific team lead's calendar, include teamLeadId parameter
      if (selectedTeamForCalendar) {
        url += `&teamLeadId=${selectedTeamForCalendar.userId}`;
      }

      // If viewing direct RMs, include directRMs parameter
      if (viewingDirectRMsInCalendar) {
        url += `&directRMs=true`;
      }

      const calendarRes = await api.get(url);
      setBranchSubmissionHistory(calendarRes.data || []);
      setCalendarDataLoaded(true);
      setIsBranchCalendarLoading(false);
    } catch (error) {
      console.error("Failed to refresh data:", error);
      setIsBranchCalendarLoading(false);
      setIsMyPerfLoading(false);
    }
  }, [
    branchCalendarStartDate,
    branchCalendarEndDate,
    selectedTeamForCalendar,
    viewingDirectRMsInCalendar,
    myPerformanceStartDate,
    myPerformanceEndDate,
  ]);

  // --- END: Refactored Data Fetching ---

  const transformPerformanceData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      userId: item.userId,
      name: item.name || item.username || "Unknown",
      totalPoints: getMetric(item, "aggregatedPoints"),
      telephoneSalesCalls: getMetric(item, "telephoneSalesCalls"),
      faceToFace: getMetric(item, "faceToFace"),
      appointmentMade: getMetric(item, "appointmentMade"),
      appointmentKept: getMetric(item, "appointmentKept"),
      factFind: getMetric(item, "factFind"),
      serviceReviews: getMetric(item, "serviceReviews"),
      socialCalls: getMetric(item, "socialCalls"),
      highIncomeWealthy: getMetric(item, "highIncomeWealthy"),
      referrals: getMetric(item, "referrals"),
      closingInterview: getMetric(item, "closingInterview"),
      salesClose: getMetric(item, "salesClose"),
      new_leads: getMetric(item, "newLeads"),
    }));
  }, []);

  // Add state for dynamic RM fetching
  const [rmsUnderTLData, setRmsUnderTLData] = useState([]);
  const [isRMDataLoading, setIsRMDataLoading] = useState(false);

  // Separate useEffect for fetching RM data when viewingTLId changes
  useEffect(() => {
    if (viewingTLId) {
      const fetchRMData = async () => {
        setIsRMDataLoading(true);
        try {
          const res = await api.get(
            `/api/users/rms-by-team-lead/${viewingTLId}?startDate=${formatDateForInput(
              branchPerformanceStartDate
            )}&endDate=${formatDateForInput(branchPerformanceEndDate)}`
          );
          setRmsUnderTLData(res.data || []);
        } catch (err) {
          console.error("Failed to fetch RM data for chart:", err);
          setRmsUnderTLData([]);
        } finally {
          setIsRMDataLoading(false);
        }
      };

      fetchRMData();
    } else {
      setRmsUnderTLData([]);
    }
  }, [viewingTLId, branchPerformanceStartDate, branchPerformanceEndDate]);

  useEffect(() => {
    let sourceData = [];

    if (viewingDirectRMsInChart) {
      // ✅ Try to use branchMembers, but fallback to performance data if empty
      const directRMsFromBranch = branchMembers.filter(
        (member) =>
          member.role === "relationship_manager" &&
          member.reportsToRole === "branch_manager" &&
          String(member.reportsToId) === localStorage.getItem("userId")
      );

      // ✅ If branchMembers is missing direct RMs, use backend data instead
      const directRMs =
        directRMsFromBranch.length > 0
          ? directRMsFromBranch
          : branchPerformanceData.filter(
              (p) =>
                p.role === "relationship_manager" &&
                p.reportsToRole === "branch_manager" &&
                String(p.reportsToId) === localStorage.getItem("userId")
            );

      sourceData = directRMs.map((rm) => {
        const perfData = branchPerformanceData.find(
          (p) => String(p.userId) === String(rm.userId)
        );
        return perfData ? { ...rm, ...perfData } : rm;
      });
    } else if (!viewingTLId) {
      // ✅ Team Lead-level performance
      sourceData = branchPerformanceData.filter((m) => m.role === "team_lead");
    } else {
      // ✅ RM-level performance under a specific Team Lead
      // Use dynamically fetched RM data from API
      if (rmsUnderTLData.length > 0) {
        sourceData = rmsUnderTLData;
      } else if (!isRMDataLoading) {
        // Fallback to find RMs from existing data
        const rmsUnderTLFromBranch = branchMembers.filter(
          (m) =>
            m.role === "relationship_manager" &&
            String(m.teamLeadId) === String(viewingTLId)
        );

        if (rmsUnderTLFromBranch.length > 0) {
          sourceData = rmsUnderTLFromBranch.map((rm) => {
            const perfData = branchPerformanceData.find(
              (p) => String(p.userId) === String(rm.userId)
            );
            return perfData ? { ...rm, ...perfData } : rm;
          });
        } else {
          // Check performance data for RMs reporting to this TL
          const rmsFromPerf = branchPerformanceData.filter(
            (m) =>
              m.role === "relationship_manager" &&
              String(m.teamLeadId) === String(viewingTLId)
          );
          sourceData = rmsFromPerf;
        }
      }
    }

    // ✅ Transform and set chart data
    setChartData(transformPerformanceData(sourceData));
  }, [
    viewingTLId,
    branchPerformanceData,
    transformPerformanceData,
    viewingDirectRMsInChart,
    branchMembers,
    isBranchPerfLoading,
    rmsUnderTLData,
    isRMDataLoading,
    branchPerformanceStartDate,
    branchPerformanceEndDate,
  ]);

  // Optimized lookup for branch members
  const branchMembersMap = useMemo(() => {
    if (!branchMembers || branchMembers.length === 0) return new Map();
    return new Map(branchMembers.map((member) => [member.userId, member]));
  }, [branchMembers]);

  // Filter pending leave requests to exclude those already approved
  const filteredPendingLeaveRequests = useMemo(() => {
    const approvedIds = new Set(
      approvedLeaveRequests.map((req) => req.leaveRequestId)
    );
    return pendingLeaveRequests.filter(
      (request) => !approvedIds.has(request.leaveRequestId)
    );
  }, [pendingLeaveRequests, approvedLeaveRequests]);
  const handleCalendarDateClick = (dateStr) => {
    let targetGroup;

    if (viewingDirectRMsInCalendar) {
      setModalTitle(
        `Direct RM Submissions for ${formatNotificationDate(dateStr)}`
      );
      targetGroup = calendarMembers;
    } else if (!selectedTeamForCalendar) {
      setModalTitle(
        `Branch-wide Team Lead Submissions for ${formatNotificationDate(
          dateStr
        )}`
      );
      targetGroup = calendarMembers.filter((m) => m.role === "team_lead");
    } else {
      setModalTitle(
        `'${
          selectedTeamForCalendar.name
        }' RM Submissions for ${formatNotificationDate(dateStr)}`
      );
      targetGroup = calendarMembers;
    }

    // 🚨 If no members are loaded, show a friendly message
    if (!targetGroup || targetGroup.length === 0) {
      setModalSubmittedList([]);
      setModalMissingList([]);
      setModalOnLeaveList([]);
      setShowMissingEntryModal(true);
      console.warn("No members found for this calendar view.");
      return;
    }

    // 🧮 Existing logic remains perfect
    const targetUserIds = new Set(targetGroup.map((m) => String(m.userId)));
    const submittedEntriesOnDate = branchSubmissionHistory.filter(
      (entry) =>
        entry.date === dateStr && targetUserIds.has(String(entry.userId))
    );

    const submittedUserIds = new Set(
      submittedEntriesOnDate.map((entry) => String(entry.userId))
    );

    const submitted = [];
    const missing = [];
    const onLeave = [];

    targetGroup.forEach((member) => {
      if (isUserOnLeaveOnDate(member, dateStr)) {
        onLeave.push(member);
      } else if (submittedUserIds.has(String(member.userId))) {
        const submission = submittedEntriesOnDate.find(
          (s) => String(s.userId) === String(member.userId)
        );
        submitted.push({ ...member, points: submission?.points || 0 });
      } else {
        missing.push(member);
      }
    });

    setModalSubmittedList(submitted);
    setModalMissingList(missing);
    setModalOnLeaveList(onLeave);
    setShowMissingEntryModal(true);
  };

  // Handlers now call targeted fetch functions
  const handleApproveLeaveRequest = async (requestId) => {
    try {
      await api.put(`/api/users/leave-request/${requestId}/approve`);
      setSnackbar({
        open: true,
        message: "Leave request approved!",
        severity: "success",
      });
      await fetchLeaveAndNotifications(); // Refresh only relevant data
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error approving leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRejectDialog = (requestId) => {
    setSelectedLeaveRequestId(requestId);
    setLeaveRejectDialogOpen(true);
  };

  const handleRejectLeaveRequest = async () => {
    if (!leaveRejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for rejection.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRequestId}/reject`,
        { rejectionReason: leaveRejectionReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request rejected.",
        severity: "success",
      });
      setLeaveRejectDialogOpen(false);
      setLeaveRejectionReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error rejecting leave request.",
        severity: "error",
      });
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!leaveRequestForm.reason.trim() || !leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Reason and start date are required.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.post("/api/users/me/leave-request", leaveRequestForm);
      setSnackbar({
        open: true,
        message: "Leave request submitted!",
        severity: "success",
      });
      setLeaveRequestDialogOpen(false);
      setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error submitting leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRevokeDialog = (requestId) => {
    setSelectedLeaveRevokeRequestId(requestId);
    setLeaveRevokeDialogOpen(true);
  };

  const handleRevokeLeaveRequest = async () => {
    if (!leaveRevocationReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for revocation.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRevokeRequestId}/revoke`,
        { revocationReason: leaveRevocationReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request revoked.",
        severity: "success",
      });
      setLeaveRevokeDialogOpen(false);
      setLeaveRevocationReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error revoking leave request.",
        severity: "error",
      });
    }
  };

  const getTopPerformers = useMemo(() => {
    let dataSource = [];
    if (viewingDirectRMsInRankings) {
      // Get direct RMs from branchMembers and match with performance data
      const directRMs = branchMembers.filter(
        (member) =>
          member.role === "relationship_manager" &&
          member.reportsToRole === "branch_manager" &&
          String(member.reportsToId) === localStorage.getItem("userId")
      );
      dataSource = directRMs.map((rm) => {
        const perfData = branchPerformanceData.find(
          (p) => p.userId === rm.userId && p.role === "relationship_manager"
        );
        return perfData ? { ...rm, ...perfData } : rm;
      });
    } else {
      dataSource = branchPerformanceData.filter(
        (member) => member.role === "team_lead"
      );
    }
    if (!dataSource.length) return [];
    return [...dataSource].sort(
      (a, b) =>
        getMetric(b, selectedLeaderboardMetric) -
        getMetric(a, selectedLeaderboardMetric)
    );
  }, [
    branchMembers,
    branchPerformanceData,
    selectedLeaderboardMetric,
    viewingDirectRMsInRankings,
  ]);

  const generateCalendarDays = useCallback(
    (historyData) => {
      const daysArray = [];
      if (
        !myPerformanceStartDate ||
        isNaN(myPerformanceStartDate) ||
        !myPerformanceEndDate ||
        isNaN(myPerformanceEndDate)
      )
        return [];
      const start = new Date(
        myPerformanceStartDate.getFullYear(),
        myPerformanceStartDate.getMonth(),
        1
      );
      const finalDate = new Date(myPerformanceEndDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const blankDays = start.getDay() === 0 ? 6 : start.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });
      let loopDate = new Date(start);
      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const isSunday = loopDate.getDay() === 0;
        const holidayObj = isIndianGovernmentHoliday(
          loopDate,
          corporateHolidays
        );
        const isHoliday = !!holidayObj;
        let status = null;
        let holidayName = null;
        if (loopDate > today) {
          if (isHoliday) {
            status = "holiday";
            holidayName = holidayObj.name;
          } else {
            status = "future";
          }
        } else if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        } else if (isSunday) {
          status = "sunday";
        } else if (historyData && historyData[dateStr]) {
          status = "submitted";
        } else {
          status = "missing";
        }
        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          status,
          dateStr,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [myPerformanceStartDate, myPerformanceEndDate, corporateHolidays]
  );

  const generateBranchOrTeamCalendarDays = useCallback(
    (historyData, memberList, teamId = null, isDirectRMView = false) => {
      const daysArray = [];
      if (
        !branchCalendarStartDate ||
        !branchCalendarEndDate ||
        isNaN(branchCalendarStartDate) ||
        isNaN(branchCalendarEndDate)
      )
        return [];
      let loopDate = new Date(
        branchCalendarStartDate.getFullYear(),
        branchCalendarStartDate.getMonth(),
        1
      );
      const finalDate = new Date(branchCalendarEndDate);
      const todayZero = new Date();
      todayZero.setHours(0, 0, 0, 0);

      const blankDays = loopDate.getDay() === 0 ? 6 : loopDate.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });

      // When teamId is provided, memberList is already pre-filtered to contain only RMs for that team
      // When teamId is null, memberList contains team leads for branch-wide view
      const targetGroup = memberList;

      const targetUserIds = new Set(targetGroup.map((m) => Number(m.userId)));
      const filteredHistoryData = historyData.filter((entry) =>
        targetUserIds.has(Number(entry.userId))
      );

      const submissionCounts = {};
      filteredHistoryData.forEach((entry) => {
        const date = entry.date;
        if (!submissionCounts[date]) submissionCounts[date] = new Set();
        submissionCounts[date].add(Number(entry.userId));
      });

      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const isSunday = loopDate.getDay() === 0;
        const holidayObj = isIndianGovernmentHoliday(loopDate);
        const isHoliday = !!holidayObj;

        const activeMembersOnDate = targetGroup.filter(
          (m) => !isUserOnLeaveOnDate(m, dateStr)
        );
        const onLeaveCount = targetGroup.length - activeMembersOnDate.length;

        const submittedOnDate = activeMembersOnDate.filter((m) =>
          submissionCounts[dateStr]?.has(Number(m.userId))
        );
        const submissionPercentage =
          activeMembersOnDate.length > 0
            ? Math.round(
                (submittedOnDate.length / activeMembersOnDate.length) * 100
              )
            : 0;

        let status = "future";
        let holidayName = null;

        if (loopDate <= todayZero) {
          if (onLeaveCount > 0 && onLeaveCount >= activeMembersOnDate.length) {
            status = "on_leave";
          } else if (submissionPercentage > 0) {
            status = "submitted";
          } else if (isHoliday) {
            status = "holiday";
            holidayName = holidayObj.name;
          } else if (isSunday) {
            status = "sunday";
          } else {
            status = "missing";
          }
        } else if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        }

        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          percentage: submissionPercentage,
          dateStr,
          status,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [branchCalendarStartDate, branchCalendarEndDate]
  );

  const calendarDays = useMemo(
    () => generateCalendarDays(submissionHistory),
    [generateCalendarDays, submissionHistory]
  );

  const branchPerformanceCalendarDays = useMemo(
    () =>
      generateBranchOrTeamCalendarDays(
        branchSubmissionHistory,
        viewingDirectRMsInCalendar ? directRMs : calendarMembers,
        selectedTeamForCalendar ? selectedTeamForCalendar.userId : null
      ),
    [
      generateBranchOrTeamCalendarDays,
      branchSubmissionHistory,
      calendarMembers,
      directRMs,
      selectedTeamForCalendar,
      viewingDirectRMsInCalendar,
    ]
  );

  const handleBackToTLs = () => {
    setViewingTLId(null);
  };

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error && currentSection === "dashboard") {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Try Again
        </Button>
      </Box>
    );
  }

  const monthlyTargetPoints = 1000;
  const monthlyTargetProgress =
    kpis && monthlyTargetPoints > 0
      ? Math.round((kpis.aggregatedPoints / monthlyTargetPoints) * 100)
      : 0;
  const conversionRate =
    kpis && kpis.totalCalls > 0
      ? Math.round((kpis.salesClosed / kpis.totalCalls) * 100)
      : 0;
  const daysInMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0
  ).getDate();
  const avgDailyCalls =
    kpis && daysInMonth > 0 ? (kpis.totalCalls / daysInMonth).toFixed(1) : 0;
  const avgSalesClose =
    kpis && daysInMonth > 0 ? (kpis.salesClosed / daysInMonth).toFixed(1) : 0;
  const dashboardTitle = `${toTitleCase(userRole)} Dashboard`;
  const userRoleDisplay = toTitleCase(userRole);

  return (
    <div>
      {isOnLeave && (
        <div
          style={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "white",
            padding: "20px 32px",
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            position: "sticky",
            top: 0,
            zIndex: 10001,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderBottom: "3px solid #ff6b35",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            minHeight: "60px",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(255, 107, 53, 0.2)",
              borderRadius: "50%",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <FiClock style={{ fontSize: "24px", color: "#ff6b35" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Currently on Leave
            </div>
            <div style={{ fontSize: "14px", opacity: "0.9" }}>
              Day {leaveDurationDays} of your leave period{" "}
              {leaveEndDate &&
                `• Returns: ${new Date(leaveEndDate).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}`}
            </div>
          </div>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-container">
              <div className="logo">
                {" "}
                <img src={logo1} alt="Logo" />{" "}
              </div>
              <span className="logo-text">Aionion Capital Markets</span>
            </div>
            <div className="header-title-group">
              <h1>{dashboardTitle}</h1>
              <span className="performance-tag">Performance Tracking</span>
            </div>
          </div>
          <div className="header-right">
            <nav className="nav-tabs">
              <button
                className={`nav-tab ${
                  currentSection === "dashboard" ? "active" : ""
                }`}
                onClick={() => navigate("/branch-manager-dashboard/dashboard")}
              >
                <FiBarChart2 /> Dashboard
              </button>
              <button
                className="nav-tab"
                onClick={() => navigate("/branch-manager-dashboard/input")}
              >
                <FiPlus /> Input
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "monthly-report" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/branch-manager-dashboard/monthly-report")
                }
              >
                <FiFileText /> Monthly Report
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "user-management" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/branch-manager-dashboard/user-management")
                }
              >
                <FiUsers /> Team Reports
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "notifications" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/branch-manager-dashboard/notifications")
                }
              >
                <FiBell /> Notifications
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "settings" ? "active" : ""
                }`}
                onClick={() => navigate("/branch-manager-dashboard/settings")}
              >
                <FiSettings /> Settings
              </button>
            </nav>
            <div className="user-profile">
              <img
                src={user.profile_photo || defaultAvatar}
                alt="Profile"
                className="profile-picture"
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
              <div className="user-info">
                <span className="user-name">{user.name || userName}</span>
                <span className="user-role">{userRoleDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="main-content">
        {(currentSection === "input" || currentSection === "input-for") && (
          <SalesTracker
            dashboardData={dashboardData}
            onBackClick={() => navigate("/branch-manager-dashboard/dashboard")}
            onEntrySubmit={reloadDataForSection}
            userId={targetUserId}
            entryDate={entryDateForTracker}
          />
        )}
        {currentSection === "dashboard" && (
          <div className="dashboard-grid">
            <section className="card todays-status-card">
              <div className="card-header">
                <h2>Today's Status</h2>
                <div className="live-indicator">
                  {(() => {
                    const date = new Date();
                    const day = date.getDate();
                    const month = date.toLocaleString("en-GB", {
                      month: "short",
                    });
                    const year = date.getFullYear();
                    return `${day} ${month},${year}`;
                  })()}
                </div>
              </div>
              <div className="status-boxes">
                {todaysStatus.submitted ? (
                  <div className="status-box submitted">
                    <div className="status-content">
                      <h3>Submitted</h3> <p>Entry for today is complete</p>
                      <span>
                        <FiClock /> Last updated at{" "}
                        {todaysStatus.submissionTime &&
                          new Date(
                            todaysStatus.submissionTime
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                      </span>
                    </div>
                    <div className="status-icon-wrapper green">
                      <FiCheckCircle />
                    </div>
                  </div>
                ) : (
                  <div className="status-box not-submitted">
                    <div className="status-content">
                      <h3>Not Submitted</h3> <p>No entry for today yet</p>
                      <span>Click Quick Entry to submit.</span>
                    </div>
                    <div className="status-icon-wrapper grey">
                      <FiClock />
                    </div>
                  </div>
                )}
                <Tooltip
                  title={
                    <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      <div>
                        Avg Daily Calls Made:{" "}
                        {monthlyKpiData.kpis
                          ? monthlyKpiData.kpis.avgDailyCalls || "0.0"
                          : "0.0"}
                      </div>
                      <div>
                        Avg Sales Closed:{" "}
                        {monthlyKpiData.kpis
                          ? monthlyKpiData.kpis.avgSalesClose || "0.0"
                          : "0.0"}
                      </div>
                      <div>
                        Total Appointments Kept:{" "}
                        {monthlyKpiData.kpis
                          ? monthlyKpiData.kpis.appointmentsKept || 0
                          : 0}
                      </div>
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontSize: "14px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                        minWidth: "220px",
                        lineHeight: "1.6",
                      },
                    },
                    arrow: { sx: { color: "#ffffff" } },
                  }}
                >
                  <div className="status-box quick-stats">
                    <div className="status-content">
                      <h3>Quick Stats</h3> <p>This month's summary</p>
                      <span>
                        <FiTrendingUp /> Avg daily points:{" "}
                        {kpis &&
                          Math.round(
                            (kpis.aggregatedPoints || 0) /
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth() + 1,
                                0
                              ).getDate()
                          )}
                      </span>
                      <span>
                        <FiAward /> Top activity:{" "}
                        {kpis &&
                          (() => {
                            const activities = {
                              totalCalls: kpis.totalCalls || 0,
                              appointmentsKept: kpis.appointmentsKept || 0,
                              faceToFaceMeetings: kpis.faceToFaceMeetings || 0,
                              salesClosed: kpis.salesClosed || 0,
                            };
                            const top = Object.keys(activities).reduce((a, b) =>
                              activities[a] > activities[b] ? a : b
                            );
                            return toTitleCase(top);
                          })()}
                      </span>
                    </div>
                    <div className="status-icon-wrapper blue">
                      <FiTrendingUp />
                    </div>
                  </div>
                </Tooltip>
              </div>
            </section>
            <section className="card quick-entry-card">
              <div
                className="quick-entry-icon"
                onClick={() => navigate("/branch-manager-dashboard/input")}
              >
                <FiPlus />
              </div>
              <h2>
                {todaysStatus.submitted ? "Edit Today's Entry" : "Quick Entry"}
              </h2>
              <button
                className="submit-entry-btn"
                onClick={() => navigate("/branch-manager-dashboard/input")}
              >
                {todaysStatus.submitted ? "Edit Entry" : "Submit Today's Entry"}
              </button>
              <span className="secure-note">
                <FiLock /> Secure & Encrypted
              </span>
            </section>

            <div className="stat-card-row">
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Aggregated Points</h4>
                  <FiAward className="stat-icon trophy" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.aggregatedPoints || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.aggregatedPoints || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Monthly Target</h4>
                  <FiTarget className="stat-icon target" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">{monthlyTargetProgress}%</span>
                </div>
                <p className="stat-period">
                  Current:{" "}
                  {monthlyKpiData.kpis
                    ? monthlyKpiData.kpis.aggregatedPoints || 0
                    : 0}{" "}
                  / {monthlyTargetPoints} points
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>New Sales Calls</h4>
                  <FiUsers className="stat-icon interactions" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.salesInteractions || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.salesInteractions || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Daily Calls</h4>
                  <FiPhoneCall
                    className="stat-icon"
                    style={{ color: "#3498db" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgDailyCalls || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgDailyCalls || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Sales Close</h4>
                  <FiTrendingUp
                    className="stat-icon"
                    style={{ color: "#2ecc71" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgSalesClose || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgSalesClose || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Appts. Kept</h4>
                  <FiCheckSquare
                    className="stat-icon"
                    style={{ color: "#9b59b6" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.appointmentsKept || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.appointmentsKept || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Conversion Rate</h4>
                  <FiBarChart2 className="stat-icon conversion" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.conversionRate || 0
                      : 0}
                    %
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.conversionRate || 0
                    : 0}
                  %
                </p>
              </section>
            </div>

            <section className="card team-lead-overview-card">
              <div className="card-header">
                <h2>Team Lead Overview</h2>
                <div className="header-controls-container">
                  <span className="date-label">From</span>
                  <input
                    type="date"
                    className="date-input"
                    value={formatDateForInput(branchDetailsStartDate)}
                    onChange={(e) =>
                      setBranchDetailsStartDate(
                        new Date(e.target.value + "T00:00:00")
                      )
                    }
                  />
                  <span className="date-label">To</span>
                  <input
                    type="date"
                    className="date-input"
                    value={formatDateForInput(branchDetailsEndDate)}
                    onChange={(e) =>
                      setBranchDetailsEndDate(
                        new Date(e.target.value + "T00:00:00")
                      )
                    }
                  />
                  <div className="search-container">
                    <span className="search-icon"></span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by name"
                      value={dashboardSearchTerm}
                      onChange={(e) => setDashboardSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      setViewingDirectRMsInTable(!viewingDirectRMsInTable)
                    }
                  >
                    {viewingDirectRMsInTable
                      ? "View Team Leads"
                      : "View Direct RMs"}
                  </Button>
                </div>
              </div>
              {isBranchDetailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography sx={{ ml: 2 }} color="text.secondary">
                    Updating Details...
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: 300, overflow: "auto", mt: 2 }}
                >
                  <Table stickyHeader sx={{ width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {viewingDirectRMsInTable
                            ? "RM Name"
                            : "Team Lead Name"}
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reports To</TableCell>
                        <TableCell>Manager Role</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">F2F</TableCell>
                        <TableCell align="right">Appt. Made</TableCell>
                        <TableCell align="right">Appt. Kept</TableCell>
                        <TableCell align="right">Fact Find</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Social</TableCell>
                        <TableCell align="right">HIW</TableCell>
                        <TableCell align="right">Referrals</TableCell>
                        <TableCell align="right">Closing Int.</TableCell>
                        <TableCell align="right">Sales Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewingDirectRMsInTable
                        ? (() => {
                            // Get direct RMs from branchMembers and match with performance data
                            const directRMs = branchMembers.filter(
                              (member) =>
                                member.role === "relationship_manager" &&
                                member.reportsToRole === "branch_manager" &&
                                String(member.reportsToId) ===
                                  localStorage.getItem("userId")
                            );
                            return directRMs.map((rm) => {
                              const perfData = branchPerformanceData.find(
                                (p) =>
                                  p.userId === rm.userId &&
                                  p.role === "relationship_manager"
                              );
                              return perfData ? { ...rm, ...perfData } : rm;
                            });
                          })()
                        : branchPerformanceData.filter(
                            (member) => member.role === "team_lead"
                          )
                      )
                        .filter((member) =>
                          (member.name || member.username || "")
                            .toLowerCase()
                            .includes(dashboardSearchTerm.toLowerCase())
                        )
                        .map((member, index) => {
                          // Get member details from branchMembers for leave status and reports info
                          const memberDetails = branchMembersMap.get(
                            member.userId
                          );
                          return (
                            <TableRow
                              key={`${
                                viewingDirectRMsInTable ? "rm" : "team-lead"
                              }-${
                                member.userId ||
                                member.id ||
                                `fallback-${index}`
                              }`}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                navigate(
                                  "/branch-manager-dashboard/user-management",
                                  {
                                    state: {
                                      preSelectedTeamLead:
                                        viewingDirectRMsInTable ? null : member,
                                      preSelectedRM: viewingDirectRMsInTable
                                        ? member
                                        : null,
                                    },
                                  }
                                )
                              }
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  color: "var(--primary-purple)",
                                }}
                              >
                                {member.name || member.username}
                              </TableCell>
                              <TableCell>
                                {memberDetails?.leaveStatus ? (
                                  <Tooltip
                                    title={`On leave until ${formatNotificationDate(
                                      memberDetails.leaveEndDate
                                    )}`}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#D97706",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <FiClock
                                        size={14}
                                        style={{ marginRight: 4 }}
                                      />{" "}
                                      On Leave
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Box
                                    sx={{
                                      color: "#16A34A",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Active
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                {memberDetails?.reportsToName ||
                                  member.reportsToName ||
                                  "N/A"}
                              </TableCell>
                              <TableCell>
                                {(memberDetails?.reportsToRole ||
                                  member.reportsToRole) === "director"
                                  ? "Director"
                                  : (memberDetails?.reportsToRole ||
                                      member.reportsToRole) === "zonal_head"
                                  ? "Zonal Head"
                                  : (memberDetails?.reportsToRole ||
                                      member.reportsToRole) === "branch_manager"
                                  ? "Branch Manager"
                                  : (memberDetails?.reportsToRole ||
                                      member.reportsToRole) === "team_lead"
                                  ? "Team Lead"
                                  : "N/A"}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "telephoneSalesCalls")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "faceToFace")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "appointmentMade")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "appointmentKept")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "factFind")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "serviceReviews")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "socialCalls")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "highIncomeWealthy")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "referrals")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "closingInterview")}
                              </TableCell>
                              <TableCell align="right">
                                {getMetric(member, "salesClose")}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </section>

            <div className="performance-row">
              <section className="card submission-history-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>My Submission History</h2>
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {calendarDays.map((dayInfo) => {
                        if (!dayInfo.day)
                          return (
                            <div
                              key={`submission-blank-${dayInfo.key}`}
                              className="calendar-day"
                            ></div>
                          );
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const entryDateObj = new Date(
                          dayInfo.dateStr.replace(/-/g, "/")
                        );
                        const daysDiff = Math.floor(
                          (today - entryDateObj) / (1000 * 60 * 60 * 24)
                        );
                        const isClickable = entryDateObj <= today;
                        const isEditable = daysDiff >= 0 && daysDiff <= 2;
                        let className = `calendar-day ${dayInfo.status || ""}`;
                        if (isClickable) className += " clickable";
                        return (
                          <div
                            key={`submission-${dayInfo.key}`}
                            className={className}
                            onClick={() =>
                              isClickable &&
                              navigate(
                                `/branch-manager-dashboard/input/${dayInfo.dateStr}`
                              )
                            }
                          >
                            {dayInfo.status === "submitted" && (
                              <FiCheckCircle />
                            )}
                            {dayInfo.status === "missing" && (
                              <span className="missing-icon">&times;</span>
                            )}
                            {dayInfo.status && !isEditable && daysDiff > 2 && (
                              <FiLock className="lock-icon" />
                            )}
                            <span>{dayInfo.day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>No Entry Made
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item holiday">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>
              <section className="card my-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>My Performance</h2>
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    <div
                      className="field-filters"
                      style={{
                        justifyContent: "center",
                        gap: "16px",
                        display: "flex",
                        marginBottom: "16px",
                      }}
                    >
                      <label htmlFor="my-performance-field-select">
                        Metric:
                      </label>
                      <select
                        id="my-performance-field-select"
                        value={selectedField1}
                        onChange={(e) => setSelectedField1(e.target.value)}
                        className="field-select"
                      >
                        {dailyPerformanceChartMetrics.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div className="field-filter">
                        <label htmlFor="field-select2">Metric 2:</label>
                        <select
                          id="field-select2"
                          value={selectedField2}
                          onChange={(e) => setSelectedField2(e.target.value)}
                          className="field-select"
                        >
                          {chartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: "var(--primary-blue)" }}
                        ></span>
                        {toTitleCase(selectedField1)}
                      </div>
                      {selectedField2 !== "none" && (
                        <div className="legend-item">
                          <span
                            className="legend-dot"
                            style={{ backgroundColor: "var(--green)" }}
                          ></span>
                          {toTitleCase(selectedField2)}
                        </div>
                      )}
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={performanceTrend}
                          margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            strokeOpacity={0.2}
                          />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                          {selectedField2 !== "none" && (
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 10 }}
                            />
                          )}
                          <ChartTooltip
                            wrapperStyle={{ zIndex: 9999 }}
                            contentStyle={{
                              backgroundColor: "var(--card-bg, #ffffff)",
                              border: "1px solid var(--border-color, #e0e0e0)",
                              borderRadius: "8px",
                              boxShadow: "var(--card-shadow)",
                            }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={selectedField1}
                            stroke="var(--primary-blue)"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={toTitleCase(selectedField1)}
                          />
                          {selectedField2 !== "none" && (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey={selectedField2}
                              stroke="var(--green)"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                              name={toTitleCase(selectedField2)}
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </section>
            </div>

            <div className="team-performance-row">
              <section className="card rm-performance-calendar-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>
                      {viewingDirectRMsInCalendar
                        ? "Direct RM Calendar"
                        : selectedTeamForCalendar
                        ? `${selectedTeamForCalendar.name}'s RM Calendar`
                        : "Branch-wide Team Lead Calendar"}
                    </h2>
                  </div>
                  <div className="header-controls-container">
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(branchCalendarStartDate)}
                      onChange={(e) =>
                        setBranchCalendarStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(branchCalendarEndDate)}
                      onChange={(e) =>
                        setBranchCalendarEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        fontSize: "10px",
                        padding: "2px 8px",
                        minWidth: "auto",
                        height: "26px",
                        lineHeight: "1.2",
                      }}
                      onClick={() => setShowTeamDialog(true)}
                    >
                      {selectedTeamForCalendar ? "Change Team" : "View by Team"}
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        fontSize: "12px",
                        padding: "2px 8px",
                        minWidth: "auto",
                        height: "30px",
                        lineHeight: "1.4",
                      }}
                      onClick={() => {
                        setViewingDirectRMsInCalendar(
                          !viewingDirectRMsInCalendar
                        );
                        if (!viewingDirectRMsInCalendar) {
                          setSelectedTeamForCalendar(null);
                        }
                      }}
                    >
                      {viewingDirectRMsInCalendar
                        ? "View Team Leads"
                        : "View Direct RMs"}
                    </Button>
                  </div>
                </div>
                {isBranchCalendarLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {branchPerformanceCalendarDays.map((dayInfo) => (
                        <Tooltip
                          key={dayInfo.key}
                          title={
                            dayInfo.status === "holiday" && dayInfo.holidayName
                              ? dayInfo.holidayName
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            className={`calendar-day ${dayInfo.status || ""} ${
                              dayInfo.day && dayInfo.status !== "future"
                                ? "clickable"
                                : ""
                            }`}
                            onClick={() =>
                              dayInfo.day &&
                              dayInfo.status &&
                              dayInfo.status !== "future" &&
                              handleCalendarDateClick(dayInfo.dateStr)
                            }
                          >
                            {dayInfo.day ? (
                              <>
                                {dayInfo.status === "submitted" && (
                                  <FiCheckCircle />
                                )}
                                {dayInfo.status === "missing" && (
                                  <span className="missing-icon">&times;</span>
                                )}
                                {dayInfo.status === "on_leave" && (
                                  <FiClock className="leave-icon" />
                                )}
                                {dayInfo.status === "sunday" && (
                                  <span className="sunday-icon">S</span>
                                )}
                                {calendarDataLoaded &&
                                  dayInfo.day &&
                                  dayInfo.status !== "future" && (
                                    <span className="points">
                                      {dayInfo.percentage}%
                                    </span>
                                  )}{" "}
                                <span>{dayInfo.day}</span>{" "}
                              </>
                            ) : (
                              <>&nbsp;</>
                            )}
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>

              <section className="card team-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>
                      {viewingDirectRMsInChart
                        ? "Direct RM Performance"
                        : viewingTLId
                        ? `${
                            branchMembers.find((m) => m.userId === viewingTLId)
                              ?.name || "Team"
                          }'s RMs`
                        : "Team Lead Performance"}
                    </h2>
                  </div>
                  <div className="card-controls">
                    {viewingDirectRMsInChart && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setViewingDirectRMsInChart(false)}
                      >
                        <FiChevronLeft style={{ marginRight: "4px" }} />
                        Back to Team Leads
                      </Button>
                    )}
                    {viewingTLId && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleBackToTLs}
                      >
                        <FiChevronLeft style={{ marginRight: "4px" }} />
                        Back
                      </Button>
                    )}
                    {!viewingDirectRMsInChart && !viewingTLId && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          setViewingDirectRMsInChart(!viewingDirectRMsInChart)
                        }
                      >
                        View Direct RMs
                      </Button>
                    )}
                    {!viewingDirectRMsInChart && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setShowChartTeamDialog(true)}
                      >
                        {viewingTLId ? "Change Team" : "View Team"}
                      </Button>
                    )}
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(branchPerformanceStartDate)}
                      onChange={(e) =>
                        setBranchPerformanceStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(branchPerformanceEndDate)}
                      onChange={(e) =>
                        setBranchPerformanceEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <label htmlFor="team-field-select">Metric:</label>
                    <select
                      id="team-field-select"
                      value={selectedPerfMetric}
                      onChange={(e) => setSelectedPerfMetric(e.target.value)}
                      className="field-select"
                    >
                      {performanceChartMetrics.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {isBranchPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : chartData && chartData.length > 0 ? (
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 20, left: -10, bottom: 50 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.2}
                        />
                        <XAxis
                          dataKey="name"
                          interval={0}
                          tick={{ fontSize: 12, angle: -35, textAnchor: "end" }}
                        />
                        <YAxis />
                        <ChartTooltip
                          cursor={{ fill: "var(--faint-bg, #f4f7fa)" }}
                          wrapperStyle={{ zIndex: 9999 }}
                          contentStyle={{
                            backgroundColor: "var(--card-bg, #ffffff)",
                            border: "1px solid var(--border-color, #e0e0e0)",
                            borderRadius: "8px",
                            boxShadow: "var(--card-shadow)",
                          }}
                        />
                        <Bar
                          dataKey={selectedPerfMetric}
                          name={
                            performanceChartMetrics.find(
                              (opt) => opt.value === selectedPerfMetric
                            )?.label || selectedPerfMetric
                          }
                          fill="#4A90E2"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div
                    className="chart-container"
                    style={{
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography color="textSecondary" gutterBottom>
                      {viewingDirectRMsInChart
                        ? "No Direct RM performance data available for the selected range."
                        : viewingTLId
                        ? "No RM data available for this Team Lead in the selected range."
                        : "No Team Lead performance data available for the selected range."}
                    </Typography>
                  </div>
                )}
              </section>
            </div>

            <section className="card leaderboard-card">
              <div className="card-header">
                <h2>
                  {viewingDirectRMsInRankings
                    ? "Direct RM Rankings"
                    : "Team Lead Rankings"}
                </h2>
                <div className="card-controls">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      setViewingDirectRMsInRankings(!viewingDirectRMsInRankings)
                    }
                    sx={{ mr: 2 }}
                  >
                    {viewingDirectRMsInRankings
                      ? "View Team Leads"
                      : "View Direct RMs"}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={selectedLeaderboardMetric}
                      onChange={(e) =>
                        setSelectedLeaderboardMetric(e.target.value)
                      }
                      displayEmpty
                    >
                      {leaderboardFieldOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              {isBranchDetailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 150,
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <div
                  className="leaderboard-list"
                  style={
                    getTopPerformers.length > 3
                      ? { maxHeight: "300px", overflowY: "auto" }
                      : {}
                  }
                >
                  {getTopPerformers.length > 0 ? (
                    getTopPerformers.map((member, index) => (
                      <div
                        key={`leaderboard-${
                          member.userId || member.id || `fallback-${index}`
                        }`}
                        className="leaderboard-item"
                      >
                        <span className="rank">{index + 1}</span>
                        <span className="name">
                          {member.name || member.username}
                        </span>
                        <span className="points">
                          {getMetric(member, selectedLeaderboardMetric) || 0}{" "}
                          {leaderboardFieldOptions
                            .find(
                              (opt) => opt.value === selectedLeaderboardMetric
                            )
                            ?.label.includes("Points")
                            ? "pts"
                            : ""}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      <Typography>
                        {viewingDirectRMsInRankings
                          ? "No direct RMs found or no performance data available"
                          : "No team leads found or no performance data available"}
                      </Typography>
                    </div>
                  )}
                </div>
              )}
              {getTopPerformers.length > 0 &&
                getTopPerformers.every(
                  (rm) => getMetric(rm, selectedLeaderboardMetric) === 0
                ) && (
                  <div
                    style={{
                      padding: "8px 16px",
                      fontSize: "12px",
                      color: "#666",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <strong>Note:</strong>{" "}
                    {viewingDirectRMsInRankings ? "Direct RMs" : "Team Leads"}{" "}
                    are ranked, but all have zero values for the selected
                    metric.
                  </div>
                )}
            </section>
          </div>
        )}

        {currentSection === "monthly-report" && <MonthlyReport />}
        {currentSection === "user-management" && (
          <BranchManagerUserManagement />
        )}
        {currentSection === "settings" && (
          <ProfilePage
            profile={user}
            profilePhotoUrl={user.profile_photo}
            onProfileUpdate={fetchInitialStaticData}
          />
        )}
        {currentSection === "notifications" && (
          <div className="notifications-section">
            <h2>Notifications & Approvals</h2>
            <div className="notifications-tabs">
              <button
                className={`tab-button ${
                  selectedNotificationTab === "pending-approvals"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("pending-approvals")}
              >
                Pending Approvals ({filteredPendingLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "approved-requests"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("approved-requests")}
              >
                Staff on Leave ({approvedLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "rm-submissions" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("rm-submissions")}
              >
                Branch Submissions
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "my-requests" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("my-requests")}
              >
                My Leave Requests
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "all-notifications"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("all-notifications")}
              >
                All Notifications
              </button>
            </div>
            {selectedNotificationTab === "my-requests" && (
              <div className="pending-requests-list">
                <h3>My Leave Requests</h3>
                {ownLeaveRequests.length > 0 ? (
                  ownLeaveRequests.map((request) => (
                    <div
                      key={request.leaveRequestId}
                      className="leave-request-item"
                    >
                      <p>
                        <strong>Your</strong> leave request from{" "}
                        <strong>
                          {formatNotificationDate(request.startDate)}
                        </strong>
                        {request.endDate ? (
                          <>
                            {" to "}
                            <strong>
                              {formatNotificationDate(request.endDate)}
                            </strong>
                          </>
                        ) : (
                          ""
                        )}
                        .
                      </p>
                      <p>
                        <strong>Reason:</strong> {request.reason}
                      </p>
                      <div className="status-badge">
                        <span className={`status ${request.status}`}>
                          {toTitleCase(request.status)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No personal leave requests found.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "rm-submissions" && (
              <div className="rm-submissions-list">
                <h3>Recent Branch Entry Submissions (Last 7 Days)</h3>
                {branchSubmissions.length > 0 ? (
                  branchSubmissions.map((submission, index) => (
                    <div
                      key={`submission-item-${index}`}
                      className="submission-item"
                    >
                      <span className="submission-indicator submitted" />
                      <div className="submission-info">
                        <Typography variant="body1">
                          <strong>{submission.rmName}</strong> submitted entry
                          for{" "}
                          <strong>
                            {formatNotificationDate(submission.date)}
                          </strong>
                          .
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {submission.points} points •{" "}
                          {submission.telephonesalescalls} calls •{" "}
                          {submission.salesclose} sales
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No recent submissions from your branch members.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "pending-approvals" && (
              <div className="pending-approvals-list">
                <h3>Leave Requests Pending Approval</h3>
                {filteredPendingLeaveRequests.length > 0 ? (
                  filteredPendingLeaveRequests.map((request) => {
                    const requestorDetails = branchMembersMap.get(
                      request.userId
                    );
                    const tlNameForRM =
                      requestorDetails?.reportsToName || "N/A";
                    return (
                      <div
                        key={`pending-${request.leaveRequestId}`}
                        className="leave-request-item"
                      >
                        <div className="request-header">
                          <Typography variant="body1">
                            {request.requestorRole === "team_lead" ? (
                              <>
                                <span className="role-badge tl-badge">
                                  Team Lead
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                              </>
                            ) : (
                              <>
                                <span className="role-badge rm-badge">RM</span>{" "}
                                <strong>{request.requestorName}</strong>{" "}
                                (reports to {tlNameForRM})
                              </>
                            )}{" "}
                            has requested leave from{" "}
                            <strong>
                              {formatNotificationDate(request.startDate)}
                            </strong>
                            {request.endDate && (
                              <>
                                {" to "}
                                <strong>
                                  {formatNotificationDate(request.endDate)}
                                </strong>
                              </>
                            )}
                            .
                          </Typography>
                          <div className="request-actions">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                handleApproveLeaveRequest(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleOpenLeaveRejectDialog(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Requested on:{" "}
                          {formatNotificationDate(request.requestedAt)}
                        </Typography>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <FiCheckCircle size={40} style={{ color: "#ccc" }} />
                    <Typography color="textSecondary" sx={{ mt: 2 }}>
                      No pending leave requests from your subordinates.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "approved-requests" && (
              <div className="approved-requests-list">
                <h3>Approved Staff Leave Requests</h3>
                {approvedLeaveRequests.length > 0 ? (
                  approvedLeaveRequests.map((request) => {
                    const tlNameForRM = request.reportsToName || "N/A";
                    return (
                      <div
                        key={`approved-${request.leaveRequestId}`}
                        className="leave-request-item"
                      >
                        <div className="request-header">
                          <Typography variant="body1">
                            {request.requestorRole === "team_lead" ? (
                              <>
                                <span className="role-badge tl-badge">
                                  Team Lead
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                              </>
                            ) : (
                              <>
                                <span className="role-badge rm-badge">RM</span>{" "}
                                <strong>{request.requestorName}</strong>{" "}
                                (reports to {tlNameForRM})
                              </>
                            )}{" "}
                            is on leave from{" "}
                            <strong>
                              {formatNotificationDate(request.startDate)}
                            </strong>
                            {request.endDate && (
                              <>
                                {" to "}
                                <strong>
                                  {formatNotificationDate(request.endDate)}
                                </strong>
                              </>
                            )}
                            .
                          </Typography>
                          <div className="request-actions">
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() =>
                                handleOpenLeaveRevokeDialog(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Revoke
                            </Button>
                          </div>
                        </div>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Approved on:{" "}
                          {formatNotificationDate(request.approvedAt)}
                        </Typography>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No approved leave requests found for your staff.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "all-notifications" && (
              <div className="notifications-list">
                <h3>All Notifications</h3>
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div key={notification.id} className="notification-item">
                      <div className="notification-content">
                        <Typography variant="body1">
                          {notification.message}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatNotificationDate(notification.date)}
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No notifications available.
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog
        open={showTeamDialog}
        onClose={() => setShowTeamDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Calendar View</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginTop: "8px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setSelectedTeamForCalendar(null);
                setShowTeamDialog(false);
              }}
            >
              View Branch-wide (Team Leads)
            </Button>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid #eee",
              }}
            />
            {branchPerformanceData
              .filter((m) => m.role === "team_lead")
              .map((tl) => (
                <Button
                  key={tl.userId}
                  variant="outlined"
                  onClick={() => {
                    setSelectedTeamForCalendar(tl);
                    setShowTeamDialog(false);
                  }}
                >
                  {tl.name || tl.username}
                </Button>
              ))}
            <div
              style={{ padding: "20px", textAlign: "center", color: "#666" }}
            >
              {branchPerformanceData.filter((m) => m.role === "team_lead")
                .length === 0 && (
                <Typography color="textSecondary">
                  No team leads available to select.
                </Typography>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTeamDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showChartTeamDialog}
        onClose={() => setShowChartTeamDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Team to View in Chart</DialogTitle>
        <DialogContent>
          {isBranchDetailsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 100,
              }}
            >
              <CircularProgress size={30} />
              <Typography sx={{ ml: 2 }} color="text.secondary">
                Loading teams...
              </Typography>
            </Box>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: "8px",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setViewingTLId(null);
                  setShowChartTeamDialog(false);
                }}
              >
                View All Team Leads
              </Button>
              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: "1px solid #eee",
                }}
              />
              {branchPerformanceData
                .filter((m) => m.role === "team_lead")
                .map((tl) => (
                  <Button
                    key={tl.userId}
                    variant="outlined"
                    onClick={() => {
                      setViewingTLId(tl.userId);
                      setShowChartTeamDialog(false);
                    }}
                  >
                    {tl.name || tl.username}
                  </Button>
                ))}
              <div
                style={{ padding: "20px", textAlign: "center", color: "#666" }}
              >
                {branchPerformanceData.filter((m) => m.role === "team_lead")
                  .length === 0 && (
                  <Typography color="textSecondary">
                    No team leads available to select.
                  </Typography>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartTeamDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showMissingEntryModal}
        onClose={() => setShowMissingEntryModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            ✅ Submitted ({modalSubmittedList.length}):
          </Typography>
          {modalSubmittedList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalSubmittedList.map((item) => (
                    <TableRow key={`submitted-item-${item.userId}`}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.points || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            🟨 On Leave ({modalOnLeaveList.length}):
          </Typography>
          {modalOnLeaveList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalOnLeaveList.map((item) => (
                    <TableRow key={`onleave-item-${item.userId}`}>
                      <TableCell>{item.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            ❌ Missing ({modalMissingList.length}):
          </Typography>
          {modalMissingList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalMissingList.map((item) => (
                    <TableRow key={`missing-item-${item.userId}`}>
                      <TableCell>{item.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              All other active members have submitted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingEntryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRequestDialogOpen}
        onClose={() => setLeaveRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Submit a leave request.
          </Typography>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLeaveRequest}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRejectDialogOpen}
        onClose={() => setLeaveRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for rejecting the leave request.
          </Typography>
          <TextField
            label="Rejection Reason"
            value={leaveRejectionReason}
            onChange={(e) => setLeaveRejectionReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectLeaveRequest}
            variant="contained"
            color="error"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRevokeDialogOpen}
        onClose={() => setLeaveRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Leave Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for revoking the approved leave request.
          </Typography>
          <TextField
            label="Revocation Reason"
            value={leaveRevocationReason}
            onChange={(e) => setLeaveRevocationReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g., Urgent project requirements, Business needs changed, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeLeaveRequest}
            variant="contained"
            color="error"
          >
            Revoke Approval
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BranchManagerDashboard;
